import {
  GatewayConformite,
  GatewayEntreprise,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import { questionnaireKeys } from "@src/data/questionnaire/hook/questionnaire.keys";
import { QuestionnaireApi } from "@src/data/questionnaire/questionnaire.api";
import {
  axeConformiteToQuestionnaireSlug,
  getAvailableAxes,
} from "@src/helper/avancement.helper";
import {
  aggregatePersonneVerificationStatus,
  getRemediationStatus,
  PersonneVerificationStatus,
} from "@src/helper/remediation.helper";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

const CONTEXT_TYPE = "ASSUJETTI";

export function usePersonnesVerificationStatus(
  personneIds: string[],
  entreprise?: GatewayEntreprise.EntrepriseAControllerResponseType
): Record<string, PersonneVerificationStatus> {
  const applicableSlugs = useMemo<
    GatewayQuestionnaire.QuestionnaireSlug[]
  >(() => {
    if (!entreprise) return [];
    return getAvailableAxes(entreprise)
      .filter(
        (axe): axe is GatewayConformite.AxeConformiteAssujetti =>
          axe in axeConformiteToQuestionnaireSlug
      )
      .map((axe) => axeConformiteToQuestionnaireSlug[axe]);
  }, [entreprise]);

  const definitionQueries = useQueries({
    queries: applicableSlugs.map((slug) => ({
      queryKey: questionnaireKeys.definition(slug),
      queryFn: () => QuestionnaireApi.getDefinition(slug),
      staleTime: Infinity,
      enabled: applicableSlugs.length > 0,
    })),
  });

  const instanceQueries = useQueries({
    queries: personneIds.map((personneId) => ({
      queryKey: questionnaireKeys.instances(CONTEXT_TYPE, personneId),
      queryFn: () => QuestionnaireApi.getInstances(CONTEXT_TYPE, personneId),
      staleTime: 30_000,
      enabled: personneIds.length > 0,
    })),
  });

  return useMemo(() => {
    const statusByPersonne: Record<string, PersonneVerificationStatus> = {};
    if (applicableSlugs.length === 0) return statusByPersonne;

    const definitionBySlug = new Map<
      string,
      GatewayQuestionnaire.QuestionnaireDefinitionResponse
    >();
    applicableSlugs.forEach((slug, idx) => {
      const definition = definitionQueries[idx]?.data;
      if (definition) definitionBySlug.set(slug, definition);
    });

    personneIds.forEach((personneId, personneIdx) => {
      const instances = instanceQueries[personneIdx]?.data ?? [];

      const axes = applicableSlugs.map((slug) => {
        const definition = definitionBySlug.get(slug);
        const slugInstances = definition
          ? instances.filter(
              (instance) => instance.questionnaireDefinitionId === definition.id
            )
          : [];
        const latest = slugInstances.length
          ? slugInstances.reduce((acc, curr) =>
              acc.createdAt > curr.createdAt ? acc : curr
            )
          : undefined;

        return {
          status: getRemediationStatus(latest),
          hasAnswers: (latest?.answers.length ?? 0) > 0,
        };
      });

      statusByPersonne[personneId] = aggregatePersonneVerificationStatus(axes);
    });

    return statusByPersonne;
  }, [personneIds, applicableSlugs, definitionQueries, instanceQueries]);
}
